import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, Edit, Trash2, X, Save, Loader2, Film, Tv, Music, Star, DollarSign, List, ArrowUpDown, Upload, AlertCircle } from 'lucide-react';
import api from '../../services/api';
import { checkYouTubeEmbeddable } from '../../utils/youtubeEmbedCheck';
import EpisodeManager from './EpisodeManager';
import FeaturedOrderManager from './FeaturedOrderManager';

const CONTENT_TYPES = [
  { value: 'movie', label: 'Movie', icon: Film },
  { value: 'tv_show', label: 'TV Show', icon: Tv },
  { value: 'music_video', label: 'Music Video', icon: Music },
];

const GENRES = [
  'Action', 'Comedy', 'Drama', 'Horror', 'Sci-Fi', 'Romance', 
  'Thriller', 'Documentary', 'Animation', 'Fantasy', 'Crime',
  'Music Video', 'Hip-Hop', 'R&B', 'Pop', 'Rock', 'Electronic', 'Country'
];

const ContentModal = ({ content, onClose, onSave, isNew = false }) => {
  const [formData, setFormData] = useState(content || {
    title: '',
    description: '',
    genre: [],
    year: new Date().getFullYear(),
    thumbnail: '',
    video_url: '',
    content_type: 'movie',
    is_premium: false,
    is_ppv: false,
    ppv_price: 4.99,
    duration: '',
    cast: [],
    director: '',
    featured: false,
  });
  const [saving, setSaving] = useState(false);
  const [castInput, setCastInput] = useState('');
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [thumbnailError, setThumbnailError] = useState('');
  const [resolvingYoutube, setResolvingYoutube] = useState(false);
  const [youtubeError, setYoutubeError] = useState('');
  const [youtubeWarning, setYoutubeWarning] = useState('');
  const [youtubeInfo, setYoutubeInfo] = useState(null);
  const thumbnailFileRef = useRef(null);

  const handleYoutubeFetch = async () => {
    const url = (formData.video_url || '').trim();
    if (!url) { setYoutubeError('Paste a YouTube URL first.'); return; }
    setYoutubeError('');
    setYoutubeWarning('');
    setYoutubeInfo(null);
    setResolvingYoutube(true);
    try {
      const res = await api.post('/admin/youtube-metadata', { url });
      handleChange('thumbnail', res.data.thumbnail_url);
      handleChange('video_url', res.data.watch_url);
      // Authoritative client-side embed probe
      const probe = await checkYouTubeEmbeddable(res.data.video_id);
      if (!probe.embeddable) {
        setYoutubeWarning(probe.reason || "This video isn't embeddable. Users won't be able to watch it. Pick a different upload.");
      } else if (res.data.yt_title) {
        setYoutubeInfo(`${res.data.yt_title}${res.data.yt_author ? ' — ' + res.data.yt_author : ''}`);
      } else {
        setYoutubeInfo('Ready to embed');
      }
    } catch (err) {
      setYoutubeError(err?.response?.data?.detail || 'Could not resolve YouTube URL.');
    } finally {
      setResolvingYoutube(false);
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setThumbnailError('Image must be 5 MB or smaller.');
      e.target.value = '';
      return;
    }
    setThumbnailError('');
    setUploadingThumbnail(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/admin/upload-thumbnail', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      handleChange('thumbnail', res.data.url);
    } catch (err) {
      setThumbnailError(err?.response?.data?.detail || 'Upload failed. Try again.');
    } finally {
      setUploadingThumbnail(false);
      if (thumbnailFileRef.current) thumbnailFileRef.current.value = '';
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenreToggle = (genre) => {
    setFormData(prev => ({
      ...prev,
      genre: prev.genre?.includes(genre)
        ? prev.genre.filter(g => g !== genre)
        : [...(prev.genre || []), genre]
    }));
  };

  const handleAddCast = () => {
    if (castInput.trim()) {
      setFormData(prev => ({
        ...prev,
        cast: [...(prev.cast || []), castInput.trim()]
      }));
      setCastInput('');
    }
  };

  const handleRemoveCast = (index) => {
    setFormData(prev => ({
      ...prev,
      cast: prev.cast.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      alert('Error saving content: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-background border border-white/10 rounded-2xl w-full max-w-3xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h3 className="text-xl font-bold text-foreground">
            {isNew ? 'Add New Content' : 'Edit Content'}
          </h3>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg"
            data-testid="close-content-modal"
          >
            <X className="h-5 w-5 text-foreground/60" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Title & Type */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-2">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                required
                className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground"
                placeholder="Enter title"
                data-testid="content-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-2">Content Type *</label>
              <div className="flex space-x-2">
                {CONTENT_TYPES.map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleChange('content_type', value)}
                    className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 rounded-lg border transition-colors ${
                      formData.content_type === value
                        ? 'bg-electric text-background border-electric'
                        : 'bg-white/5 border-white/20 text-foreground hover:bg-white/10'
                    }`}
                    data-testid={`content-type-${value}`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-sm">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-2">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              required
              rows={3}
              className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground resize-none"
              placeholder="Enter description"
              data-testid="content-description-input"
            />
          </div>

          {/* Year & Duration */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-2">Year *</label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => handleChange('year', parseInt(e.target.value))}
                required
                min="1900"
                max="2030"
                className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground"
                data-testid="content-year-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-2">Duration</label>
              <input
                type="text"
                value={formData.duration || ''}
                onChange={(e) => handleChange('duration', e.target.value)}
                placeholder="e.g., 2h 15m"
                className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground"
                data-testid="content-duration-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-2">Director</label>
              <input
                type="text"
                value={formData.director || ''}
                onChange={(e) => handleChange('director', e.target.value)}
                placeholder="Director name"
                className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground"
                data-testid="content-director-input"
              />
            </div>
          </div>

          {/* Thumbnail URL */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-2">Thumbnail *</label>
            <div className="flex gap-2">
              <input
                type="url"
                value={formData.thumbnail}
                onChange={(e) => handleChange('thumbnail', e.target.value)}
                required
                className="flex-1 px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground"
                placeholder="Paste an image URL or click Upload"
                data-testid="content-thumbnail-input"
              />
              <input
                ref={thumbnailFileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleThumbnailUpload}
                className="hidden"
                data-testid="content-thumbnail-file"
              />
              <button
                type="button"
                onClick={() => thumbnailFileRef.current?.click()}
                disabled={uploadingThumbnail}
                className="px-4 py-2 bg-electric/15 hover:bg-electric/25 border border-electric/50 text-electric rounded-lg flex items-center gap-2 disabled:opacity-60 whitespace-nowrap"
                data-testid="content-thumbnail-upload-btn"
              >
                {uploadingThumbnail
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                  : <><Upload className="w-4 h-4" /> Upload</>
                }
              </button>
            </div>
            {thumbnailError && (
              <div className="mt-2 text-sm text-red-400" data-testid="content-thumbnail-error">{thumbnailError}</div>
            )}
            <div className="mt-1 text-xs text-foreground/50">
              JPEG, PNG, WebP, or GIF • max 5 MB • stored on durable object storage
            </div>
            {formData.thumbnail && (
              <div className="mt-2">
                <img
                  src={formData.thumbnail}
                  alt="Preview"
                  className="h-24 rounded-lg object-cover"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
          </div>

          {/* Video URL (for non-Mux content) */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-2">Video URL</label>
            <div className="flex gap-2">
              <input
                type="url"
                value={formData.video_url || ''}
                onChange={(e) => handleChange('video_url', e.target.value)}
                className="flex-1 px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground"
                placeholder="Paste a YouTube link, MP4 URL, or use Video Upload for Mux"
                data-testid="content-video-url-input"
              />
              <button
                type="button"
                onClick={handleYoutubeFetch}
                disabled={resolvingYoutube || !(formData.video_url || '').trim()}
                className="px-4 py-2 bg-red-500/15 hover:bg-red-500/25 border border-red-500/50 text-red-400 rounded-lg flex items-center gap-2 disabled:opacity-50 whitespace-nowrap"
                data-testid="content-youtube-fetch-btn"
                title="Fetch thumbnail from YouTube"
              >
                {resolvingYoutube
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Fetching…</>
                  : <>▶ Use YouTube</>
                }
              </button>
            </div>
            {youtubeError && (
              <div className="mt-2 text-sm text-red-400" data-testid="content-youtube-error">{youtubeError}</div>
            )}
            {youtubeWarning && (
              <div className="mt-2 flex items-start gap-2 p-2 rounded-lg bg-orange-500/10 border border-orange-500/30" data-testid="content-youtube-warning">
                <AlertCircle className="h-4 w-4 text-orange-400 shrink-0 mt-0.5" />
                <p className="text-sm text-orange-300">{youtubeWarning}</p>
              </div>
            )}
            {youtubeInfo && !youtubeWarning && (
              <div className="mt-2 text-sm text-electric" data-testid="content-youtube-info">
                ✓ Embed check passed · {youtubeInfo}
              </div>
            )}
            <div className="mt-1 text-xs text-foreground/50">
              For music videos: paste a YouTube link and click <span className="text-red-400">Use YouTube</span> — it will auto-fetch the thumbnail. Otherwise leave blank if uploading via the Video Upload tab.
            </div>
          </div>

          {/* Genres */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-2">Genres</label>
            <div className="flex flex-wrap gap-2">
              {GENRES.map(genre => (
                <button
                  key={genre}
                  type="button"
                  onClick={() => handleGenreToggle(genre)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    formData.genre?.includes(genre)
                      ? 'bg-electric text-background'
                      : 'bg-white/10 text-foreground hover:bg-white/20'
                  }`}
                  data-testid={`genre-${genre}`}
                >
                  {genre}
                </button>
              ))}
            </div>
          </div>

          {/* Cast */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-2">Cast</label>
            <div className="flex space-x-2 mb-2">
              <input
                type="text"
                value={castInput}
                onChange={(e) => setCastInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCast())}
                placeholder="Add cast member"
                className="flex-1 px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground"
                data-testid="content-cast-input"
              />
              <button
                type="button"
                onClick={handleAddCast}
                className="px-4 py-2 bg-white/10 text-foreground rounded-lg hover:bg-white/20"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.cast?.map((member, index) => (
                <span 
                  key={index}
                  className="flex items-center space-x-1 px-3 py-1 bg-white/10 rounded-full text-sm"
                >
                  <span>{member}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveCast(index)}
                    className="text-red-400 hover:text-red-300"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Access & Pricing */}
          <div className="bg-white/5 rounded-xl p-4 space-y-4">
            <h4 className="text-foreground font-medium flex items-center space-x-2">
              <DollarSign className="h-4 w-4 text-green-400" />
              <span>Access & Pricing</span>
            </h4>
            
            <div className="grid md:grid-cols-3 gap-4">
              {/* Featured */}
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.featured || false}
                  onChange={(e) => handleChange('featured', e.target.checked)}
                  className="w-5 h-5 rounded border-white/20 bg-white/5 text-electric focus:ring-electric"
                  data-testid="content-featured-checkbox"
                />
                <div>
                  <span className="text-foreground">Featured</span>
                  <p className="text-xs text-muted-foreground">Show in hero section</p>
                </div>
              </label>

              {/* Premium */}
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_premium || false}
                  onChange={(e) => handleChange('is_premium', e.target.checked)}
                  className="w-5 h-5 rounded border-white/20 bg-white/5 text-electric focus:ring-electric"
                  data-testid="content-premium-checkbox"
                />
                <div>
                  <span className="text-foreground">Premium Only</span>
                  <p className="text-xs text-muted-foreground">Subscribers only</p>
                </div>
              </label>

              {/* PPV */}
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_ppv || false}
                  onChange={(e) => handleChange('is_ppv', e.target.checked)}
                  className="w-5 h-5 rounded border-white/20 bg-white/5 text-electric focus:ring-electric"
                  data-testid="content-ppv-checkbox"
                />
                <div>
                  <span className="text-foreground">Pay-Per-View</span>
                  <p className="text-xs text-muted-foreground">One-time purchase</p>
                </div>
              </label>
            </div>

            {/* PPV Price */}
            {formData.is_ppv && (
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-2">PPV Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.99"
                  value={formData.ppv_price || 4.99}
                  onChange={(e) => handleChange('ppv_price', parseFloat(e.target.value))}
                  className="w-32 px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground"
                  data-testid="content-ppv-price-input"
                />
              </div>
            )}

            {/* Release Date */}
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-2">Release Date (PST)</label>
              <p className="text-xs text-muted-foreground mb-2">Content will be locked until this date. Leave empty for immediate availability.</p>
              <input
                type="datetime-local"
                value={formData.release_date ? new Date(formData.release_date).toISOString().slice(0, 16) : ''}
                onChange={(e) => {
                  if (e.target.value) {
                    const pstDate = new Date(e.target.value + ':00-07:00');
                    handleChange('release_date', pstDate.toISOString());
                  } else {
                    handleChange('release_date', null);
                  }
                }}
                className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground"
                data-testid="content-release-date-input"
              />
              {formData.release_date && (
                <button
                  type="button"
                  onClick={() => handleChange('release_date', null)}
                  className="mt-1 text-xs text-red-400 hover:text-red-300"
                >
                  Clear release date
                </button>
              )}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-white/10">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 bg-white/10 text-foreground rounded-lg hover:bg-white/20 transition-colors"
            data-testid="cancel-content-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-2 bg-electric text-background rounded-lg font-medium hover:bg-electric/90 transition-colors disabled:opacity-50"
            data-testid="save-content-btn"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{isNew ? 'Create Content' : 'Save Changes'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const ContentManagement = ({ content, onDeleteContent, onRefreshData }) => {
  const navigate = useNavigate();
  const [editingContent, setEditingContent] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [managingEpisodes, setManagingEpisodes] = useState(null);
  const [showFeaturedOrder, setShowFeaturedOrder] = useState(false);

  const handleEditContent = async (updatedContent) => {
    const { id, ...updateData } = updatedContent;
    await api.put(`/admin/content/${id}`, updateData);
    onRefreshData?.();
  };

  const handleCreateContent = async (newContent) => {
    await api.post('/admin/content', newContent);
    onRefreshData?.();
  };

  const filteredContent = content.filter(item => {
    const matchesSearch = !searchQuery || 
      item.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = !filterType || item.content_type === filterType;
    return matchesSearch && matchesType;
  });

  return (
    <div className="space-y-6">
      {/* Featured Order Manager Modal */}
      {showFeaturedOrder && (
        <FeaturedOrderManager onClose={() => setShowFeaturedOrder(false)} />
      )}

      {/* Header & Controls */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <h3 className="text-lg font-semibold text-foreground">Content Management</h3>
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => setShowFeaturedOrder(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded-lg font-medium hover:bg-purple-500/30 transition-colors"
              data-testid="featured-order-btn"
            >
              <ArrowUpDown className="h-4 w-4" />
              <span>Featured Order</span>
            </button>
            <button 
              onClick={() => setIsAddingNew(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-electric text-background rounded-lg font-medium hover:bg-electric/90 transition-colors"
              data-testid="add-content-btn"
            >
              <Plus className="h-4 w-4" />
              <span>Add Content</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground"
              data-testid="content-search-input"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground"
            data-testid="content-type-filter"
          >
            <option value="">All Types</option>
            <option value="movie">Movies</option>
            <option value="tv_show">TV Shows</option>
            <option value="music_video">Music Videos</option>
          </select>
        </div>
        
        {/* Content List */}
        <div className="grid gap-4">
          {filteredContent.map((item) => (
            <div key={item.id} className="flex items-center space-x-4 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors">
              <img
                src={item.thumbnail}
                alt={item.title}
                className="w-24 h-14 object-cover rounded-lg"
              />
              <div className="flex-1 min-w-0">
                <h4 className="text-foreground font-medium truncate">{item.title}</h4>
                <div className="flex items-center flex-wrap gap-2 text-sm text-muted-foreground">
                  <span className="capitalize">{item.content_type?.replace('_', ' ')}</span>
                  <span>•</span>
                  <span>{item.year}</span>
                  {item.featured && (
                    <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                      <Star className="h-3 w-3 inline mr-1" />Featured
                    </span>
                  )}
                  {item.is_premium && (
                    <span className="px-2 py-0.5 bg-electric/20 text-electric text-xs rounded-full">Premium</span>
                  )}
                  {item.is_ppv && (
                    <span className="px-2 py-0.5 bg-amber-400/20 text-amber-400 text-xs rounded-full">
                      PPV ${item.ppv_price}
                    </span>
                  )}
                  {item.mux_status === 'ready' && (
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">Mux Ready</span>
                  )}
                  {item.content_type === 'tv_show' && (
                    <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                      {item.episode_count || 0} episodes
                    </span>
                  )}
                </div>
              </div>
              <div className="flex space-x-2 flex-shrink-0">
                <button 
                  onClick={() => navigate(`/content/${item.id}`)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="View"
                  data-testid={`view-content-${item.id}`}
                >
                  <Eye className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                </button>
                {item.content_type === 'tv_show' && (
                  <button 
                    onClick={() => setManagingEpisodes(item)}
                    className="p-2 hover:bg-purple-500/20 rounded-lg transition-colors"
                    title="Manage Episodes"
                    data-testid={`episodes-content-${item.id}`}
                  >
                    <List className="h-4 w-4 text-purple-400" />
                  </button>
                )}
                <button 
                  onClick={() => setEditingContent(item)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Edit"
                  data-testid={`edit-content-${item.id}`}
                >
                  <Edit className="h-4 w-4 text-muted-foreground hover:text-electric" />
                </button>
                <button
                  onClick={() => onDeleteContent(item.id)}
                  className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                  title="Delete"
                  data-testid={`delete-content-${item.id}`}
                >
                  <Trash2 className="h-4 w-4 text-red-400" />
                </button>
              </div>
            </div>
          ))}
          {filteredContent.length === 0 && (
            <p className="text-center text-muted-foreground py-8">
              {searchQuery || filterType ? 'No content matches your filters' : 'No content found'}
            </p>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editingContent && (
        <ContentModal
          content={editingContent}
          onClose={() => setEditingContent(null)}
          onSave={handleEditContent}
        />
      )}

      {/* Add New Modal */}
      {isAddingNew && (
        <ContentModal
          isNew
          onClose={() => setIsAddingNew(false)}
          onSave={handleCreateContent}
        />
      )}

      {/* Episode Manager Modal (TV Shows only) */}
      {managingEpisodes && (
        <EpisodeManager
          contentId={managingEpisodes.id}
          contentTitle={managingEpisodes.title}
          onClose={() => setManagingEpisodes(null)}
        />
      )}
    </div>
  );
};

export default ContentManagement;
